import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITournamentsState, ITournament } from "./types";
import {
  fetchGetAvailableTournaments,
  fetchGetMyApplicationsTournaments,
  fetchGetCreatedTournaments,
  fetchCreateTournament,
  fetchUpdateTournament,
  fetchDeleteTournament,
  fetchApplyTournament,
  fetchCancelTournamentApplication,
  fetchGetTournamentApplicationsCount,
  fetchGetUserTournamentStatus,
  fetchChangeUserTournamentStatus,
  fetchGetTournamentTypes,
} from "./asyncActions";

const initialState: ITournamentsState = {
  tournamentsLoading: false,
  availableTournaments: [],
  myApplicationsTournaments: [],
  myCreatedTournaments: [],
  userTournamentStatus: null,
  applicationsCount: 0,
  tournamentTypes: [],
};

const tournamentsSlice = createSlice({
  name: "tournaments",
  initialState,
  reducers: {
    setTournamentsLoading(state, action: PayloadAction<boolean>) {
      state.tournamentsLoading = action.payload;
    },
    setAvailableTournaments(state, action: PayloadAction<ITournament[]>) {
      state.availableTournaments = action.payload;
    },
    setMyApplicationsTournaments(state, action: PayloadAction<ITournament[]>) {
      state.myApplicationsTournaments = action.payload;
    },
    setMyCreatedTournaments(state, action: PayloadAction<ITournament[]>) {
      state.myCreatedTournaments = action.payload;
    },
    setUserTournamentStatus(state, action: PayloadAction<string | null>) {
      state.userTournamentStatus = action.payload;
    },
    setApplicationsCount(state, action: PayloadAction<number>) {
      state.applicationsCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGetAvailableTournaments.pending, (state) => {
        // Не устанавливаем loading здесь, так как он устанавливается вручную перед всеми запросами
      })
      .addCase(fetchGetAvailableTournaments.fulfilled, (state, action) => {
        const filtered = action.payload.filter((t) => !t.is_enjoying);
        state.availableTournaments = filtered;
        // Не сбрасываем loading здесь, так как могут быть другие запросы
      })
      .addCase(fetchGetAvailableTournaments.rejected, (state) => {
        state.tournamentsLoading = false;
      })
      .addCase(fetchGetMyApplicationsTournaments.fulfilled, (state, action) => {
        state.myApplicationsTournaments = action.payload;
        // После завершения всех запросов loading будет сброшен вручную
      })
      .addCase(fetchGetMyApplicationsTournaments.rejected, (state) => {
        state.tournamentsLoading = false;
      })
      .addCase(fetchGetCreatedTournaments.fulfilled, (state, action) => {
        state.myCreatedTournaments = action.payload;
        // После завершения всех запросов loading будет сброшен вручную
      })
      .addCase(fetchGetCreatedTournaments.rejected, (state) => {
        state.tournamentsLoading = false;
      })
      .addCase(fetchCreateTournament.fulfilled, (state, action) => {
        state.myCreatedTournaments.push(action.payload);
      })
      .addCase(fetchUpdateTournament.fulfilled, (state, action) => {
        const index = state.myCreatedTournaments.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.myCreatedTournaments[index] = action.payload;
        }
      })
      .addCase(fetchDeleteTournament.fulfilled, (state, action) => {
        state.myCreatedTournaments = state.myCreatedTournaments.filter(
          (t) => t.id !== action.payload
        );
      })
      .addCase(fetchCancelTournamentApplication.fulfilled, (state, action) => {
        state.myApplicationsTournaments = state.myApplicationsTournaments.filter(
          (t) => t.id !== action.payload
        );
      })
      .addCase(
        fetchGetTournamentApplicationsCount.fulfilled,
        (state, action) => {
          state.applicationsCount = action.payload.count || 0;
        }
      )
      .addCase(fetchGetUserTournamentStatus.fulfilled, (state, action) => {
        state.userTournamentStatus = action.payload;
      })
      .addCase(fetchChangeUserTournamentStatus.fulfilled, (state, action) => {
        if (action.payload) {
          state.userTournamentStatus = action.payload;
        }
      })
      .addCase(fetchGetTournamentTypes.fulfilled, (state, action) => {
        state.tournamentTypes = action.payload;
      });
  },
});

export const {
  setTournamentsLoading,
  setAvailableTournaments,
  setMyApplicationsTournaments,
  setMyCreatedTournaments,
  setUserTournamentStatus,
  setApplicationsCount,
} = tournamentsSlice.actions;

export default tournamentsSlice.reducer;
